import { Hash } from 'lucide-react';

interface YapTopicLabelProps {
  /** The forum this yap was cross-posted to. */
  forumId: string;
  forumName: string;
  /** Extra classes for spacing in the host card layout. */
  className?: string;
}

/**
 * YapTopicLabel — the `# ForumName` pill shown on any yap that was tagged with a
 * Forum "topic" at compose time. Tapping it navigates to that Forum.
 *
 * Rendered through the shared card sub-components so the behaviour is identical
 * everywhere a yap appears (YapTab, Forum, Tribe, Yap Details). It navigates via
 * the existing `navigateToHistoryForum` event (handled in AppRouter), which
 * re-fetches the forum doc by id — so id + name is all we need to pass.
 */
export function YapTopicLabel({ forumId, forumName, className = '' }: YapTopicLabelProps) {
  if (!forumId) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(
      new CustomEvent('navigateToHistoryForum', { detail: { id: forumId, title: forumName } })
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      // Colour is set inline (not just via a Tailwind class) so it always wins
      // over the card's inherited near-white text — the # and name stay blue,
      // distinct from the post body.
      style={{ color: '#4478ea' }}
      className={`inline-flex items-center gap-1 max-w-full px-2.5 py-1 rounded-full text-[13px] font-normal leading-none transition-colors
        bg-[#4478ea]/10 dark:bg-[#4478ea]/15
        hover:bg-[#4478ea]/20 dark:hover:bg-[#4478ea]/25 ${className}`}
    >
      <Hash className="w-3 h-3 shrink-0" style={{ color: '#4478ea' }} />
      <span className="truncate">{forumName}</span>
    </button>
  );
}

export default YapTopicLabel;
